import React, {type FC, type PropsWithChildren, type ReactNode, useCallback} from 'react';

import {AtNewIcon} from '@time-webkit/all/icons/at-new';
import {TildeNewIcon} from '@time-webkit/all/icons/tilde-new';
import {HashtagNewIcon} from '@time-webkit/all/icons/hashtag-new';

import {useAppDispatch, useAppSelector} from 'stores/redux_store';
import {toggleFormattingBar} from 'features/editor/slices/editor_settings';

import {hasFormattingBar} from 'features/editor/selectors/has_formatting_bar';
import {FormatEditContourNewIcon} from '@time-webkit/all/icons/format-edit-contour-new';
import {FormatEditFullNewIcon} from '@time-webkit/all/icons/format-edit-full-new';
import {KEYBOARD_SHORTCUTS} from 'components/keyboard_shortcuts/keyboard_shortcuts_sequence';

import {sendToStatist} from 'packages/statist';

import {useEditorContext} from '../../context';
import {Divider} from '../divider';
import {ActionButton} from '../action-button';
import {ShortcutTooltip} from '../shortcut-tooltip';

import {OverflowContainer} from '../overflow-container';

import styles from './styles.module.css';

type Props = {
    emojiPicker?: ReactNode;
    fileUploader?: ReactNode;
    onClosePopover?: (event: MouseEvent) => void;
}

export const ActionsBar: FC<PropsWithChildren<Props>> = ({
    emojiPicker,
    fileUploader,
    children,
    onClosePopover,
}) => {
    const {features, editor} = useEditorContext();
    const {mention} = features;

    const dispatch = useAppDispatch();
    const isFormattingBarOpened = useAppSelector(hasFormattingBar);

    const handleToggleFormattingBar = useCallback(() => {
        dispatch(toggleFormattingBar());
        sendToStatist('editor.format.display', {hidden: isFormattingBarOpened});
        return editor?.chain().focus().run();
    }, [dispatch, editor, isFormattingBarOpened]);

    const handleMention = useCallback(() => {
        return editor?.commands.setMention();
    }, [editor]);

    const handleChannelMention = useCallback(() => {
        return editor?.commands.setChannelMention();
    }, [editor]);

    const handleHashtagMention = useCallback(() => {
        return editor?.commands.setHashtagMention();
    }, [editor]);

    return (
        <div className={styles.bar}>
            <OverflowContainer
                className={styles.leftActions}
                hiddenNodes={
                    <>
                        <ShortcutTooltip key={0} descriptor={KEYBOARD_SHORTCUTS.mentionChannel}>
                            <ActionButton
                                onPress={handleChannelMention}
                                icon={<TildeNewIcon />}
                                aria-label='Mention channel'
                            />
                        </ShortcutTooltip>
                        <ShortcutTooltip key={1} descriptor={KEYBOARD_SHORTCUTS.addHashtag}>
                            <ActionButton
                                onPress={handleHashtagMention}
                                icon={<HashtagNewIcon />}
                                aria-label='Add hashtag'
                            />
                        </ShortcutTooltip>
                    </>
                }
                onClose={onClosePopover}
                data-testid='editor_actions_bar'
            >
                <ShortcutTooltip
                    descriptor={isFormattingBarOpened ? (
                        KEYBOARD_SHORTCUTS.hideFormattingBar
                    ) : KEYBOARD_SHORTCUTS.openFormattingBar}
                    data-testid='editor_formatting_bar_toggle_button'
                >
                    <ActionButton
                        aria-label='Toggle formatting bar'
                        onPress={handleToggleFormattingBar}
                        icon={isFormattingBarOpened ? <FormatEditContourNewIcon /> : <FormatEditFullNewIcon />}
                    />
                </ShortcutTooltip>
                <Divider />
                {fileUploader}
                {emojiPicker}
                {mention?.isActive ? (
                    <ShortcutTooltip descriptor={KEYBOARD_SHORTCUTS.mentionUser}>
                        <ActionButton
                            aria-label='Mention'
                            onPress={handleMention}
                            icon={<AtNewIcon />}
                        />
                    </ShortcutTooltip>
                ) : null}
            </OverflowContainer>

            <div className={styles.rightActions}>{children}</div>
        </div>
    );
};
